import { Injectable } from '@angular/core';

import { BehaviorSubject, fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { breakpoint, remToPx } from '@util/layout';

export interface LayoutSize {
  width: number;
  height: number;
}

export interface HeaderDimensions {
  width: number;
  height: number;
  marginLeft: number;
  marginRight: number;
}

@Injectable({
  providedIn: 'root',
})
export class WindowService {
  devicePixelRatio$ = new BehaviorSubject<number>(window.devicePixelRatio);

  screenSize$ = new BehaviorSubject<LayoutSize>({ width: window.screen.width, height: window.screen.height });
  screenHeight$ = new BehaviorSubject<number>(window.screen.height);
  screenWidth$ = new BehaviorSubject<number>(window.screen.width);

  viewportSize$ = new BehaviorSubject<LayoutSize>({ width: window.visualViewport.width, height: window.visualViewport.height });
  viewportHeight$ = new BehaviorSubject<number>(window.visualViewport.height);
  viewportWidth$ = new BehaviorSubject<number>(window.visualViewport.width);

  bodySize$ = new BehaviorSubject<LayoutSize>({ width: document.body.scrollWidth, height: document.body.scrollHeight });
  bodyHeight$ = new BehaviorSubject<number>(document.body.scrollHeight);
  bodyWidth$ = new BehaviorSubject<number>(document.body.scrollWidth);

  headerDimensions$ = new BehaviorSubject<HeaderDimensions>({ width: 0, height: 0, marginLeft: 0, marginRight: 0 });

  mobile: boolean = false;
  mobile$ = new BehaviorSubject<boolean>(false);

  constructor() {
    this.init();
  }

  init() {
    fromEvent(window, 'resize')
      .pipe(debounceTime(50))
      .subscribe((event: any) => {
        this.detectDeviceSize();

        this.viewportSize$.next({ width: event.target.visualViewport.width, height: event.target.visualViewport.height });
        this.viewportHeight$.next(event.target.visualViewport.height);
        this.viewportWidth$.next(event.target.visualViewport.width);

        this.bodySize$.next({ width: event.target.document.body.scrollWidth, height: event.target.document.body.scrollHeight });
        this.bodyHeight$.next(event.target.document.body.scrollHeight);
        this.bodyWidth$.next(event.target.document.body.scrollWidth);

        this.getHeaderDimensions();
      });

    fromEvent(window, 'load')
      .pipe(debounceTime(50))
      .subscribe(() => {
        this.detectDeviceSize();

        this.viewportSize$.next({ width: window.visualViewport.width, height: window.visualViewport.height });
        this.viewportHeight$.next(window.visualViewport.height);
        this.viewportWidth$.next(window.visualViewport.width);

        this.bodySize$.next({ width: document.body.scrollWidth, height: document.body.scrollHeight });
        this.bodyHeight$.next(document.body.scrollHeight);
        this.bodyWidth$.next(document.body.scrollWidth);

        this.getHeaderDimensions();
      });

    this.viewportWidth$.pipe().subscribe((viewportWidth) => {
      if (viewportWidth <= 576) {
        this.mobile = true;
        this.mobile$.next(true);
      } else {
        this.mobile = false;
        this.mobile$.next(false);
      }
    });
  }

  detectDeviceSize() {
    this.devicePixelRatio$.next(window.devicePixelRatio);
    this.screenHeight$.next(window.screen.height);
    this.screenWidth$.next(window.screen.width);
  }

  getHeaderDimensions() {
    const headerPrimary = document.querySelector<HTMLDivElement>('.header-primary');
    const headerSecondary = document.querySelector<HTMLDivElement>('.header-secondary');

    let width = 0;
    let height = 0;

    let marginLeft = 0;
    let marginRight = 0;

    if (headerPrimary) {
      width += headerPrimary.offsetWidth;
      height += headerPrimary.offsetHeight;

      marginLeft += (window.visualViewport.width - headerPrimary.offsetWidth) / 2;
      marginRight += (window.visualViewport.width - headerPrimary.offsetWidth) / 2;
    }

    if (headerSecondary && window.visualViewport.width > breakpoint.sm) {
      height += headerSecondary.offsetHeight;
    }

    // add rem to account for padding / margins
    let padding = 0;

    if (window.visualViewport.width <= breakpoint.sm) {
      padding = remToPx(0.5);
    } else if (window.visualViewport.width <= breakpoint.md) {
      padding = remToPx(1);
    } else {
      padding = remToPx(2);
    }

    height += padding;

    this.headerDimensions$.next({ width, height, marginLeft, marginRight });

    return { width, height, marginLeft, marginRight };
  }

  getElementDimensions(selector: string) {
    const element = document.querySelector<HTMLElement>(selector);

    let width = 0;
    let height = 0;

    if (element) {
      width += element.offsetWidth;
      height += element.offsetHeight;
    }

    return { width, height };
  }
}
